import type { ProjectRecord } from "@/lib/api";
import { DOMAIN_LABEL, shortDate, shortDay } from "@/lib/overview";
import { css, cx } from "@styled-system/css";

interface ProjectRowProps {
  project: ProjectRecord;
  selected: boolean;
  onSelect: (slug: string) => void;
}

export const LIST_GRID = "[84px minmax(0, 1fr) 44px 60px 68px 52px]";

const row = css({
  display: "grid",
  gridTemplateColumns: LIST_GRID,
  gap: "10",
  px: "12",
  py: "10",
  alignItems: "baseline",
  borderBottom: "default",
  textAlign: "left",
  w: "full",
  bg: "transparent",
  border: "none",
  cursor: "pointer",
  _hover: { bg: "surface.hovered" },
});
const rowSelected = css({
  bg: "surface.raised",
  boxShadow: "[inset 2px 0 0 var(--oks-colors-border-selected)]",
});

const DOMAIN_INK = {
  cetec: css({ color: "domain.cetec" }),
  mkbd: css({ color: "domain.mkbd" }),
  personal: css({ color: "domain.personal" }),
} as const;

const mono = css({
  fontFamily: "mono",
  fontSize: "12",
  lineHeight: "default",
  color: "text.subtlest",
});
const name = css({
  fontSize: "14",
  lineHeight: "default",
  fontWeight: "medium",
  color: "text.bold",
});
const sub = css({ fontSize: "12", lineHeight: "default", color: "text.subtle", mt: "2" });
const reentry = css({
  display: "grid",
  gridTemplateColumns: "[36px minmax(0, 1fr)]",
  gap: "[1px 8px]",
  mt: "2",
  fontSize: "12",
  lineHeight: "default",
});

export function ProjectRow({ project, selected, onSelect }: ProjectRowProps) {
  const personal = project.open === null;
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cx(row, selected && rowSelected)}
      onClick={() => onSelect(project.slug)}
    >
      <span className={cx(mono, DOMAIN_INK[project.domain], css({ fontSize: "12" }))}>
        {DOMAIN_LABEL[project.domain]}
      </span>
      <span className={css({ minW: "0", display: "block" })}>
        <span className={name}>{project.name}</span>
        {personal ? (
          <span className={reentry}>
            <span className={cx(mono, css({ fontSize: "12" }))}>last</span>
            <span className={css({ color: "text.subtle" })}>{project.last ?? "—"}</span>
            <span className={cx(mono, css({ fontSize: "12" }))}>next</span>
            <span className={css({ color: "text" })}>{project.next ?? "—"}</span>
          </span>
        ) : (
          project.note && <span className={cx(sub, css({ display: "block" }))}>{project.note}</span>
        )}
      </span>
      <span className={cx(mono, !personal && project.open ? css({ color: "text" }) : undefined)}>
        {personal ? "·" : (project.open ?? "—")}
      </span>
      <span className={cx(mono, project.waiting > 0 && css({ color: "text.warning" }))}>
        {personal && project.waiting === 0 ? "·" : project.waiting || "—"}
      </span>
      <span className={cx(mono, project.nextDate && css({ color: "text.warning" }))}>
        {shortDay(project.nextDate)}
      </span>
      <span className={mono}>{shortDate(project.updated)}</span>
    </button>
  );
}
